/**
 * MobileTrigger — touch invocation for dddk on phones / tablets.
 *
 * Gesture: **rapid up-down swipe** — the user wiggles a finger up and down
 * several times in quick succession (≥3 direction reversals within ~700ms).
 * This is chosen because:
 *   - Normal scroll is one direction at a time → no false positives
 *   - Doesn't collide with any OS-reserved gesture (iOS back, Android nav,
 *     pull-to-refresh, etc.)
 *   - Discoverable by hint or onboarding ("shake your finger up and down")
 *
 * On trigger, two semi-transparent chrome bars slide in:
 *
 *   ┌──────────────────────────────────────────┐
 *   │ 🔍 Palette                            ✕ │  ← top bar
 *   ├──────────────────────────────────────────┤
 *   │                                          │
 *   │   (original page content — still         │
 *   │    scrollable; **tapping any element     │
 *   │    triggers dwell-style**        │
 *   │    instead of the element's own click)   │
 *   │                                          │
 *   ├──────────────────────────────────────────┤
 *   │              ●  (center button)          │  ← bottom bar
 *   └──────────────────────────────────────────┘
 *
 * Top bar:
 *   - Tap "🔍 Palette" → opens command palette
 *   - Tap "✕"          → closes the chrome, page returns to normal
 *
 * Middle (the page):
 *   - Scrolling: works as before
 *   - Tapping an element: intercepted → fires `palette_open` with the
 *     element's selector so Dwell / a host skill can annotate
 *
 * Bottom bar — circular center button (replaces Space):
 *   - Long press (> 200ms)  → fires `voice_start`
 *   - Single tap            → fires `gesture_accept`
 *   - Double tap (within 350ms) → fires `gesture_reject`
 *
 * @example
 * ```ts
 * import { MobileTrigger } from '@perhapxin/dddk';
 *
 * const mt = new MobileTrigger();
 * mt.attachTo(dddk);
 * ```
 */

import type { DotDotDuck } from '../orchestrator';
import { injectScopedStyle } from '../utils/dom';
import { inferSelector } from '../utils/selector';
import { renderHeroGreetingBody, type HeroGreetingConfig, type HeroGreetingRow } from './mobile-hero-greeting';
export type { HeroGreetingConfig, HeroGreetingRow };

/**
 * Floating action button (FAB) — the default mobile entry point.
 *
 * Replaces nothing; runs alongside the swipe gesture so users have two ways
 * in. Highly customizable: icon, label, position, shape, color, full CSS
 * override. Hosts can also call `bindTrigger(myButton)` to use their OWN
 * existing header / nav element as the trigger, then set `fab: false` to
 * suppress the default FAB.
 */
export interface MobileFABConfig {
  /** Show the default FAB? Default true on touch devices. */
  enabled?: boolean;
  /**
   * The icon shown inside the FAB. Accepts:
   *   - emoji / text string ('🔍', '✨')
   *   - raw HTML string ('<svg>...</svg>')
   *   - an HTMLElement (will be appended into the button)
   * Default: a 24px sparkle SVG.
   */
  icon?: string | HTMLElement;
  /** Optional visible text under/next to the icon. Default none. */
  text?: string;
  /** ARIA label. Default 'Open command palette'. */
  ariaLabel?: string;
  /** Position. Default 'bottom-right'. */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** Offset from the edges in px. Default { x: 20, y: 24 }. */
  offset?: { x?: number; y?: number };
  /** Diameter in px (square shape uses this as width/height). Default 56. */
  size?: number;
  /** Shape. Default 'circle'. 'square' uses --dddk-radius. 'pill' = rounded rect. */
  shape?: 'circle' | 'square' | 'pill';
  /**
   * Inline CSS override — applied AFTER all defaults. Use this to fully
   * replace background, gradient, border, shadow, etc.
   */
  style?: Partial<CSSStyleDeclaration>;
  /** Extra className(s) to append; lets hosts target with their own CSS. */
  className?: string;
  /**
   * What the FAB does on tap. Default: open palette.
   * Receive the DotDotDuck instance so host can do anything.
   */
  onTap?: (dddk: DotDotDuck) => void;
  /**
   * What the FAB does on long-press (>200ms by default).
   * Default: fires the same voice-start event the center button fires.
   * Set to `null` to disable long-press behavior entirely.
   */
  onLongPress?: ((dddk: DotDotDuck) => void) | null;
  /**
   * Map of face slug → image URL. When set, the FAB renders the
   * `neutral` face by default and `setFace(slug)` swaps between them.
   * Overrides `icon` when present. Typical use: point at deployed
   * face assets so the FAB becomes an expressive duck-head avatar.
   *
   *   faces: {
   *     neutral: '/duck/neutral.png',
   *     thinking: '/duck/thinking.png',
   *     listening: '/duck/listening.png',
   *     done: '/duck/done.png',
   *     error: '/duck/error.png',
   *     smile: '/duck/smile.png',
   *     wink: '/duck/wink.png',
   *   }
   *
   * Missing slugs are ignored — `setFace('foo')` on an unknown slug
   * is a no-op instead of clearing the image.
   */
  faces?: Record<string, string>;
  /**
   * Also mount the FAB on desktop viewports. Default `false` — the
   * legacy behavior only shows it on mobile / touch. Set to `true`
   * when the FAB is the primary presence for the SDK (mascot mode).
   */
  alwaysVisible?: boolean;
}

export interface MobileTriggerConfig {
  /** Min direction reversals in the swipe window to trigger. Default 3. */
  reversalsToTrigger?: number;
  /** Time window (ms) the reversals must happen within. Default 700. */
  swipeWindowMs?: number;
  /** Min Y delta per reversal (px). Default 30. Filters jitter. */
  minSwipeDelta?: number;
  /**
   * Long-press threshold for voice (ms). Default 500 — matches
   * Apple/Google convention for "hold vs tap". Was 200 in early
   * versions but real fingers linger 250-350ms on a normal tap,
   * which incorrectly triggered voice-start.
   */
  longPressMs?: number;
  /** Double-tap window (ms). Default 350. */
  doubleTapMs?: number;
  /** Force enable on non-touch devices (for testing). Default false. */
  forceEnable?: boolean;
  /**
   * When chrome is open, tapping page elements routes through Dwell (host
   * wires the listener). Default `false` — chrome shows but taps work
   * normally. Flip via `setDwellOnTap()` at runtime.
   */
  dwellOnTap?: boolean;
  /**
   * Floating action button — default mobile entry point.
   * Pass `false` to disable (host uses `bindTrigger()` with their own UI).
   * Pass an object to customize.
   */
  fab?: MobileFABConfig | false;

  /**
   * First-visit HERO greeting. Auto-fires on first `attachTo()` if the
   * user hasn't seen it this session (sessionStorage-gated). Pass:
   *   - `false` — disable entirely.
   *   - `{}` — use every SDK default (bilingual title + tap-me / hold-me
   *     action rows + keyboard hint on desktop).
   *   - `{ title, rows, kbdHint, autoDismissMs, delayMs, storageKey, bodyHtml }`
   *     — override any axis; unspecified fields fall back to defaults.
   *
   * Styling — every visual is a CSS variable declared in `tokens.css`
   * (`--dddk-fab-hero-title-size`, `--dddk-fab-hero-row-gap`, ... etc).
   * Override on `:root` instead of touching the config.
   *
   * See `docs/v0.2.2/dddk/mascot.md` for the full contract.
   */
  heroGreeting?: HeroGreetingConfig | false;
}

import { UI_ATTR } from '../utils/dom';
const STYLE_ID = 'dddk-mobile-trigger-style';

interface SwipeSample {
  y: number;
  t: number;
  dir: 'up' | 'down' | null;
}

/** Semantic states the mascot FAB can be in; drives face + CSS animation. */
export type FabState =
  | 'idle'
  | 'thinking'
  | 'listening'
  | 'voice-hold'
  | 'done'
  | 'error';

const STATE_TO_FACE: Record<FabState, string> = {
  'idle': 'neutral',
  'thinking': 'thinking',
  'listening': 'listening',
  'voice-hold': 'listening',
  'done': 'done',
  'error': 'error',
};

export class MobileTrigger {
  private cfg: Omit<Required<MobileTriggerConfig>, 'fab'> & { fab: MobileFABConfig | false };
  private dddk: DotDotDuck | null = null;
  private topBar: HTMLDivElement | null = null;
  private bottomBar: HTMLDivElement | null = null;
  private fabEl: HTMLButtonElement | null = null;
  private fabImg: HTMLImageElement | null = null;
  private greetingEl: HTMLDivElement | null = null;
  private greetingDismiss: (() => void) | null = null;
  private currentFace: string = 'neutral';
  private currentState: FabState = 'idle';
  private visible = false;
  private samples: SwipeSample[] = [];
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private lastTapAt = 0;
  private singleTapTimer: ReturnType<typeof setTimeout> | null = null;
  private cleanups: Array<() => void> = [];

  private dwellOnTap: boolean;

  constructor(config: MobileTriggerConfig = {}) {
    this.cfg = {
      reversalsToTrigger: config.reversalsToTrigger ?? 2,
      swipeWindowMs: config.swipeWindowMs ?? 900,
      minSwipeDelta: config.minSwipeDelta ?? 18,
      longPressMs: config.longPressMs ?? 500,
      doubleTapMs: config.doubleTapMs ?? 350,
      forceEnable: config.forceEnable ?? false,
      dwellOnTap: config.dwellOnTap ?? false,
      fab: config.fab ?? {},
      // Default: auto-fire the built-in bilingual greeting on first
      // visit. `false` disables it entirely; passing an object
      // overrides just the fields you care about.
      heroGreeting: config.heroGreeting ?? {},
    };
    this.dwellOnTap = this.cfg.dwellOnTap;
  }

  setDwellOnTap(enabled: boolean): void {
    this.dwellOnTap = enabled;
  }

  isDwellOnTap(): boolean {
    return this.dwellOnTap;
  }

  attachTo(dddk: DotDotDuck): void {
    if (typeof document === 'undefined') return;

    // Important: we ALWAYS attach + listen for breakpoint changes, even
    // when the initial viewport is desktop. Otherwise:
    //   1. Page loads on desktop → attachTo bails out
    //   2. User opens DevTools, switches to mobile emulation → no listener
    //      to react, FAB stays absent forever
    //   3. User reports "the FAB disappeared"
    // We still gate the actual FAB MOUNT on touch/mobile/forceEnable, but
    // the matchMedia listener is unconditional so any later transition
    // into mobile mode lights up the FAB.
    this.dddk = dddk;
    ensureStyles();

    const touch = isTouchDevice();
    const mobile = isMobileViewport();
    const force = !!this.cfg.forceEnable;
    const fabCfg = this.cfg.fab === false ? null : this.cfg.fab;
    const alwaysVisible = !!fabCfg?.alwaysVisible;
    // `alwaysVisible: true` — new mascot mode. The FAB is the primary
    // presence for the SDK, so we mount it on every viewport, including
    // desktop with a real mouse. Legacy behavior (touch/mobile only)
    // still applies when alwaysVisible is false or unset.
    const shouldMountNow = alwaysVisible || force || touch || mobile;

    if (touch || force) {
      this.bindSwipeDetector();
    }

    if (
      shouldMountNow &&
      this.cfg.fab !== false &&
      (this.cfg.fab.enabled ?? true)
    ) {
      this.mountFab();
    }

    // Always-on breakpoint listener — handles desktop→mobile transitions
    // (DevTools emulation, window resize, orientation change). Skipped
    // when alwaysVisible is on because the FAB never dismounts.
    if (!alwaysVisible) {
      const mq = window.matchMedia('(max-width: 768px)');
      const onChange = () => {
        const shouldShow = mq.matches || isTouchDevice() || this.cfg.forceEnable;
        if (shouldShow && !this.fabEl && this.cfg.fab !== false && (this.cfg.fab.enabled ?? true)) {
          this.mountFab();
        } else if (!shouldShow && this.fabEl) {
          this.fabEl.remove();
          this.fabEl = null;
          this.fabImg = null;
        }
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else (mq as MediaQueryList).addListener?.(onChange);
      this.cleanups.push(() => {
        if (mq.removeEventListener) mq.removeEventListener('change', onChange);
        else (mq as MediaQueryList).removeListener?.(onChange);
      });
    }

    // Fire the first-visit HERO greeting (if enabled + not seen this
    // session). Deferred + session-storage-gated inside the helper.
    this.scheduleFirstVisitGreeting();
  }

  /**
   * Auto-fire the built-in HERO greeting on first attach if the visitor
   * hasn't seen it this session. Reads `this.cfg.heroGreeting`:
   *
   *   - `false`             → skip entirely
   *   - `undefined` / `{}`  → use every SDK default
   *   - `{ autoShow: false }` → keep the config for later programmatic
   *     firing via `showHeroGreetingWithDefaults()`, but skip auto-fire
   *   - anything else — override just the fields you care about
   *
   * Session-storage-gated so returning visitors aren't nagged. Skips
   * silently on SSR / storage-blocked contexts.
   */
  private scheduleFirstVisitGreeting(): void {
    const cfg = this.cfg.heroGreeting;
    if (cfg === false) return;
    const config: HeroGreetingConfig = typeof cfg === 'object' && cfg !== null ? cfg : {};
    if (config.autoShow === false) return;

    const storageKey = config.storageKey ?? 'dddk_hero_greeting_seen_v1';
    try {
      if (typeof window !== 'undefined' && window.sessionStorage?.getItem(storageKey)) return;
    } catch { /* storage blocked (private browsing / CSP) — treat as first-visit */ }

    const delayMs = config.delayMs ?? 1200;
    setTimeout(() => {
      if (!this.fabEl) return;
      void this.showHeroGreetingWithDefaults(config);
      try {
        if (typeof window !== 'undefined') window.sessionStorage?.setItem(storageKey, '1');
      } catch { /* storage blocked — greeting will fire on every visit, acceptable */ }
    }, delayMs);
  }

  /**
   * Show the greeting using the SDK's built-in bilingual template.
   * Called by the auto-fire path, but also public — hosts can call it
   * on demand (e.g. from a "Replay tour" palette entry). Passing
   * `overrides` lets you swap just the fields you care about without
   * touching the trigger's config.
   */
  showHeroGreetingWithDefaults(overrides: HeroGreetingConfig = {}): Promise<void> {
    const config: HeroGreetingConfig = {
      ...(typeof this.cfg.heroGreeting === 'object' && this.cfg.heroGreeting !== null ? this.cfg.heroGreeting : {}),
      ...overrides,
    };
    const locale = this.readLocale();
    const isTouch =
      typeof window !== 'undefined'
      && window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;
    const { bodyHtml, ariaLabel } = renderHeroGreetingBody(config, locale, { isTouch });
    return this.showHeroGreeting(ariaLabel, {
      bodyHtml,
      ariaLabel,
      autoDismissMs: config.autoDismissMs ?? 20000,
    });
  }

  /**
   * Read the current locale from the attached DotDotDuck instance,
   * falling back to `document.documentElement.lang` or `'en'`.
   */
  private readLocale(): string {
    if (this.dddk) {
      const loc = (this.dddk as unknown as { _config?: { locale?: string } })._config?.locale;
      if (loc) return loc;
    }
    if (typeof document !== 'undefined') {
      const lang = document.documentElement.lang;
      if (lang) return lang;
    }
    return 'en';
  }

  /**
   * Bind ANY host element as a palette trigger. Use this when you want your
   * existing header button / hamburger menu / nav icon to also open dddk.
   * Returns a cleanup function.
   *
   *   const off = mt.bindTrigger(document.querySelector('#my-fab')!);
   *   // later: off();
   */
  bindTrigger(
    element: HTMLElement,
    opts: {
      onTap?: (dddk: DotDotDuck) => void;
      onLongPress?: ((dddk: DotDotDuck) => void) | null;
    } = {},
  ): () => void {
    if (!this.dddk) {
      // Defer until attached.
      this.cleanups.push(() => {});
      return () => {};
    }
    const dddk = this.dddk;
    const onTap = opts.onTap ?? ((d) => d.palette.toggle());
    const onLongPress = opts.onLongPress === undefined
      ? (d: DotDotDuck) => d.triggerVoiceStart()
      : opts.onLongPress;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let longFired = false;

    const press = () => {
      longFired = false;
      if (onLongPress) {
        timer = setTimeout(() => {
          timer = null;
          longFired = true;
          onLongPress(dddk);
        }, this.cfg.longPressMs);
      }
    };
    const release = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (!longFired) onTap(dddk);
      else if (onLongPress) dddk.triggerVoiceEnd();
    };
    const cancel = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const onTouchStart = (e: TouchEvent) => { e.preventDefault(); press(); };
    const onTouchEnd = (e: TouchEvent) => { e.preventDefault(); release(); };

    element.addEventListener('touchstart', onTouchStart, { passive: false });
    element.addEventListener('touchend', onTouchEnd, { passive: false });
    element.addEventListener('touchcancel', cancel);
    element.addEventListener('mousedown', press);
    element.addEventListener('mouseup', release);
    element.addEventListener('mouseleave', cancel);

    const off = () => {
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchend', onTouchEnd);
      element.removeEventListener('touchcancel', cancel);
      element.removeEventListener('mousedown', press);
      element.removeEventListener('mouseup', release);
      element.removeEventListener('mouseleave', cancel);
    };
    this.cleanups.push(off);
    return off;
  }

  show(): void {
    if (!this.dddk) return;
    if (!this.topBar) this.mountChrome();
    this.visible = true;
    this.topBar?.setAttribute('data-visible', '');
    this.bottomBar?.setAttribute('data-visible', '');
  }

  hide(): void {
    this.visible = false;
    this.topBar?.removeAttribute('data-visible');
    this.bottomBar?.removeAttribute('data-visible');
  }

  toggle(): void {
    this.visible ? this.hide() : this.show();
  }

  destroy(): void {
    for (const c of this.cleanups) c();
    this.cleanups = [];
    this.topBar?.remove();
    this.bottomBar?.remove();
    this.fabEl?.remove();
    this.topBar = this.bottomBar = null;
    this.fabEl = null;
  }

  // ─── FAB (floating action button) ───────────────────────────────

  private mountFab(): void {
    if (!this.dddk || this.fabEl) return;
    const fabCfg: MobileFABConfig = this.cfg.fab === false ? {} : this.cfg.fab;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute(UI_ATTR, 'mobile-fab');
    btn.setAttribute('aria-label', fabCfg.ariaLabel ?? 'Open command palette');
    if (fabCfg.className) btn.className = fabCfg.className;

    // Position
    const pos = fabCfg.position ?? 'bottom-right';
    const ox = fabCfg.offset?.x ?? 20;
    const oy = fabCfg.offset?.y ?? 24;
    if (pos.includes('top'))    btn.style.top = `calc(env(safe-area-inset-top, 0px) + ${oy}px)`;
    if (pos.includes('bottom')) btn.style.bottom = `calc(env(safe-area-inset-bottom, 0px) + ${oy}px)`;
    if (pos.includes('left'))   btn.style.left = `${ox}px`;
    if (pos.includes('right'))  btn.style.right = `${ox}px`;

    // Size + shape
    const size = fabCfg.size ?? 56;
    if (fabCfg.shape === 'pill') {
      btn.style.minWidth = `${size}px`;
      btn.style.height = `${size}px`;
      btn.style.borderRadius = `${size / 2}px`;
      btn.style.padding = '0 20px';
    } else {
      btn.style.width = `${size}px`;
      btn.style.height = `${size}px`;
      btn.style.borderRadius = fabCfg.shape === 'square' ? 'var(--dddk-radius, 12px)' : '50%';
    }

    // Icon content
    const inner = document.createElement('span');
    inner.style.display = 'inline-flex';
    inner.style.alignItems = 'center';
    inner.style.justifyContent = 'center';
    inner.style.width = '100%';
    inner.style.height = '100%';
    inner.style.gap = '6px';
    inner.style.pointerEvents = 'none';

    // Face mode — swap between multiple duck-face images at runtime.
    // Takes precedence over `icon` when configured; supports live
    // swap via setFace(). The <img> fills the button (background is
    // transparent so only the yellow duck circle shows).
    const faces = fabCfg.faces;
    if (faces && faces.neutral) {
      const img = document.createElement('img');
      img.src = faces.neutral;
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';
      img.style.display = 'block';
      // Instant swap on setFace — no fade so state indicators feel snappy.
      inner.appendChild(img);
      this.fabImg = img;
      // Face-mode buttons are transparent background — the duck head
      // is the whole visual, we don't want a colored disc under it.
      btn.style.background = 'transparent';
      btn.style.boxShadow = 'none';
      btn.setAttribute('data-face', 'neutral');
      btn.setAttribute('data-state', 'idle');
      // One-shot pop-in on mount so the FAB isn't just there — it
      // arrives. `data-enter="fresh"` triggers the scale-bounce
      // keyframe; we clear it on animationend so the idle bob resumes
      // cleanly instead of both animations racing.
      btn.setAttribute('data-enter', 'fresh');
      btn.addEventListener('animationend', function onEnter(e) {
        if ((e as AnimationEvent).animationName === 'dddk-fab-enter') {
          btn.removeAttribute('data-enter');
          btn.removeEventListener('animationend', onEnter as EventListener);
        }
      });
      // (Eye tracking overlay attempt reverted — sprite already has
      // eyes drawn in, so adding two more pupils overlaid on top
      // produced a fly-eye look. Doing this properly needs a
      // separately-baked base sprite with empty eye sockets. Deferred
      // to whenever we regenerate the neutral face asset with that
      // constraint.)
    } else if (fabCfg.icon instanceof HTMLElement) {
      inner.appendChild(fabCfg.icon);
    } else if (typeof fabCfg.icon === 'string') {
      // Allow either raw SVG/HTML or short text/emoji.
      if (fabCfg.icon.trim().startsWith('<')) inner.innerHTML = fabCfg.icon;
      else inner.textContent = fabCfg.icon;
    } else {
      inner.innerHTML = DEFAULT_FAB_SVG;
    }
    if (fabCfg.text) {
      const t = document.createElement('span');
      t.textContent = fabCfg.text;
      t.style.fontSize = '13px';
      t.style.fontWeight = '600';
      inner.appendChild(t);
    }
    btn.appendChild(inner);

    // Host-supplied style overrides (LAST so they always win).
    if (fabCfg.style) Object.assign(btn.style, fabCfg.style);

    // Behavior — use the existing bindTrigger machinery.
    document.body.appendChild(btn);
    this.fabEl = btn;
    this.bindTrigger(btn, { onTap: fabCfg.onTap, onLongPress: fabCfg.onLongPress });
  }

  /** Hide / show the default FAB at runtime. */
  setFabVisible(visible: boolean): void {
    if (this.fabEl) this.fabEl.style.display = visible ? '' : 'none';
  }

  /** Return the default FAB element (so hosts can decorate it further). */
  getFabElement(): HTMLButtonElement | null {
    return this.fabEl;
  }

  // ─── mascot face + state ────────────────────────────────────────

  /**
   * Swap the FAB face to the given slug. No-op when the FAB isn't in
   * face mode (no `faces` config) or the slug is unknown — the current
   * face stays. Returns true when an actual swap happened.
   */
  setFace(slug: string): boolean {
    const cfg = this.cfg.fab === false ? null : this.cfg.fab;
    const url = cfg?.faces?.[slug];
    if (!url || !this.fabImg || !this.fabEl) return false;
    if (this.currentFace === slug) return true;
    this.fabImg.src = url;
    this.currentFace = slug;
    this.fabEl.setAttribute('data-face', slug);
    return true;
  }

  /** Current face slug (or 'neutral' if face mode is off). */
  getFace(): string { return this.currentFace; }

  /**
   * Set the semantic state — swaps the face AND toggles the CSS
   * animation class on the FAB. Every state change also fires a
   * one-shot ripple burst (water ring emitted from the FAB centre)
   * so the transition reads as physical, not a bare CSS class swap.
   *
   *   idle       — gentle bob loop (default)
   *   thinking   — slow rotate + thinking face
   *   listening  — subtle pulse + listening face
   *   voice-hold — balloon inflate (long-press feedback)
   *   done       — quick pop + done face
   *   error      — small shake + error face
   */
  setState(state: FabState): void {
    if (!this.fabEl) return;
    const prev = this.currentState;
    this.currentState = state;
    this.fabEl.setAttribute('data-state', state);
    this.setFace(STATE_TO_FACE[state]);
    if (prev !== state) this.emitRipple();
  }

  /** One-shot ripple burst from the FAB centre. CSS-driven — we just
   *  bump `data-ripple` on the button so the pseudo-element animation
   *  replays. Auto-clears after the animation duration. */
  private emitRipple(): void {
    if (!this.fabEl) return;
    const btn = this.fabEl;
    // Replay-replay trick: remove attr, force reflow, re-add.
    btn.removeAttribute('data-ripple');
    void btn.offsetWidth;
    btn.setAttribute('data-ripple', '');
    setTimeout(() => btn.removeAttribute('data-ripple'), 900);
  }

  // (Eye tracking `setupEyeTracking` method removed — see note in
  // mountFab. Kept the ripple burst + glance choreography which don't
  // require sprite rework.)

  /** Current mascot state. */
  getState(): FabState { return this.currentState; }

  // ─── greeting speech bubble ─────────────────────────────────────

  /**
   * Render a speech bubble next to the FAB with a text greeting. The
   * bubble is anchored to the FAB's edge and stays until the user
   * clicks anywhere (default) or the optional autoDismiss timeout
   * elapses.
   *
   * Also briefly plays a wave animation on the FAB (face = smile,
   * scale bounce) so the greeting reads as an active hello, not a
   * passive tooltip.
   *
   * Resolves once the user dismisses the greeting. If the FAB isn't
   * mounted, resolves immediately.
   */
  /**
   * "Big duck reveal" greeting for first-visit / brand moments. The
   * small FAB circle briefly recedes and a large side-view rubber-duck
   * sprite grows out of the same corner, taking over ~30% of the
   * viewport. A speech bubble anchors to its left with the greeting
   * text. Click anywhere → duck shrinks back into the FAB circle,
   * which fades back in.
   *
   * `heroSrc` — image URL for the big sprite; defaults to reading
   *   `--dddk-swim-url` from :root (fallback: `--dddk-avatar-url`).
   *   Skip this arg when the CSS variable is already wired.
   */
  showHeroGreeting(
    text: string,
    opts: { autoDismissMs?: number; heroSrc?: string; ariaLabel?: string; bodyHtml?: string } = {},
  ): Promise<void> {
    // Redesigned again: the FAB circle STRETCHES horizontally into a
    // yellow-translucent capsule/pill. The duck face stays on the
    // right (where the FAB was); the greeting text fills the space
    // that opens up on its left. No separate bubble element — the
    // FAB button IS the greeting surface. Everything themeable via
    // CSS variables (see `--dddk-fab-hero-*` in the docs).
    void opts.heroSrc;
    if (!this.fabEl || typeof document === 'undefined') return Promise.resolve();
    this.hideGreeting();

    const fab = this.fabEl;
    const prevFace = this.currentFace;
    const prevState = this.currentState;

    // Inject a text panel inside the FAB — sits absolutely-positioned
    // covering the pill's non-face half. Uses aria-live so screen
    // readers announce it.
    const content = document.createElement('div');
    content.setAttribute(UI_ATTR, 'fab-hero-content');
    content.setAttribute('role', 'status');
    content.setAttribute('aria-live', 'polite');
    if (opts.ariaLabel) content.setAttribute('aria-label', opts.ariaLabel);
    if (opts.bodyHtml) {
      content.innerHTML = opts.bodyHtml;
    } else {
      content.textContent = text;
    }
    fab.appendChild(content);
    this.greetingEl = content;

    // Toggle the hero attribute — all shape / colour / motion is CSS.
    // Reset any lingering transform + pause the bob so the width
    // transition owns the animation.
    fab.style.animation = 'none';
    fab.style.transform = 'none';
    void fab.offsetWidth;
    fab.setAttribute('data-hero-active', '');
    // Prefer the side-left (logo pose) face so the duck's bill points
    // AT the text on its left — reads as "the duck is talking".
    // Falls back to smile if the host hasn't wired the slug.
    if (!this.setFace('side-left')) {
      this.setFace('smile');
    }

    return new Promise<void>((resolve) => {
      const dismiss = () => {
        // Reverse: capsule shrinks back to circle. Content fades then
        // the pill contracts to circle. See CSS on [data-hero-active]
        // + [data-hero-dismissing].
        fab.setAttribute('data-hero-dismissing', '');
        fab.removeAttribute('data-hero-active');
        this.setFace(prevFace);
        this.setState(prevState);
        setTimeout(() => {
          fab.style.animation = '';
          fab.style.transform = '';
          fab.removeAttribute('data-hero-dismissing');
          content.remove();
          if (this.greetingEl === content) this.greetingEl = null;
          resolve();
        }, 520);
      };
      this.greetingDismiss = dismiss;

      // Clicking the DUCK FACE (right end of the pill) fires the
      // host's FAB onTap AND dismisses — reads as "the mascot invites
      // you to open the palette". Clicking anywhere else on the pill
      // (text, whitespace) or off-pill dismisses without opening.
      const onPageClick = (e: Event) => {
        const t = e.target as Node | null;
        const clickedFab = t && fab.contains(t) && !content.contains(t);
        document.removeEventListener('click', onPageClick, true);
        document.removeEventListener('touchstart', onPageClick, true);
        if (clickedFab) {
          try {
            const cfg = this.cfg.fab === false ? null : this.cfg.fab;
            const dddk = this.dddk;
            if (dddk) {
              if (cfg?.onTap) cfg.onTap(dddk);
              else dddk.palette.toggle();
            }
          } catch { /* swallow — dismiss anyway */ }
        }
        dismiss();
      };
      // 400ms delay skips the entrance-event flurry (framework focus /
      // autofill dispatches) that used to auto-dismiss the greeting
      // before it was fully visible.
      setTimeout(() => {
        document.addEventListener('click', onPageClick, true);
        document.addEventListener('touchstart', onPageClick, true);
      }, 400);

      if (opts.autoDismissMs && opts.autoDismissMs > 0) {
        setTimeout(() => {
          document.removeEventListener('click', onPageClick, true);
          document.removeEventListener('touchstart', onPageClick, true);
          dismiss();
        }, opts.autoDismissMs);
      }
    });
  }

  showGreeting(
    text: string,
    opts: { autoDismissMs?: number; faceSlug?: string; ariaLabel?: string } = {},
  ): Promise<void> {
    if (!this.fabEl || typeof document === 'undefined') return Promise.resolve();
    // Squash any prior greeting first.
    this.hideGreeting();

    const bubble = document.createElement('div');
    bubble.setAttribute(UI_ATTR, 'mobile-fab-greeting');
    bubble.setAttribute('role', 'status');
    bubble.setAttribute('aria-live', 'polite');
    if (opts.ariaLabel) bubble.setAttribute('aria-label', opts.ariaLabel);
    bubble.textContent = text;
    document.body.appendChild(bubble);
    this.greetingEl = bubble;

    // Position the bubble relative to the FAB. FAB position is fixed;
    // we compute an anchor point on the FAB's INNER edge (the edge
    // facing the page content) so the bubble tail points at the duck.
    const positionBubble = () => {
      if (!this.fabEl || !this.greetingEl) return;
      const rect = this.fabEl.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const onRight = rect.left > vw / 2;
      const onBottom = rect.top > vh / 2;
      const gap = 12;
      // Horizontal anchor: bubble to the side of the FAB that faces the
      // center of the page. If the FAB sits on the right half, bubble
      // goes to its LEFT; on the left half, bubble goes RIGHT.
      if (onRight) {
        this.greetingEl.style.right = `${vw - rect.left + gap}px`;
        this.greetingEl.style.left = 'auto';
        this.greetingEl.setAttribute('data-side', 'left');
      } else {
        this.greetingEl.style.left = `${rect.right + gap}px`;
        this.greetingEl.style.right = 'auto';
        this.greetingEl.setAttribute('data-side', 'right');
      }
      // Vertical anchor: aligned with the top of the FAB or slightly
      // above so the tail reads as "coming from the mascot's mouth".
      if (onBottom) {
        this.greetingEl.style.bottom = `${vh - rect.top - 12}px`;
        this.greetingEl.style.top = 'auto';
      } else {
        this.greetingEl.style.top = `${rect.top - 12}px`;
        this.greetingEl.style.bottom = 'auto';
      }
    };
    positionBubble();
    const onResize = () => positionBubble();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    // Give the FAB a moment of expressive greeting — wave face + a
    // little scale bounce. Both revert on dismiss.
    const wasFace = this.currentFace;
    const wasState = this.currentState;
    this.setFace(opts.faceSlug ?? 'smile');
    this.fabEl.setAttribute('data-greeting', '');

    // Reveal after next frame so CSS transition on data-visible works.
    requestAnimationFrame(() => bubble.setAttribute('data-visible', ''));

    return new Promise<void>((resolve) => {
      const dismiss = () => {
        this.hideGreeting();
        // Revert face/state to what they were before the greeting.
        this.setFace(wasFace);
        this.setState(wasState);
        window.removeEventListener('resize', onResize);
        window.removeEventListener('scroll', onResize, true);
        resolve();
      };
      this.greetingDismiss = dismiss;

      // Anywhere-click dismisses the greeting. Capture-phase so it
      // wins against page handlers; skip clicks on the FAB itself so
      // the FAB's own tap handler still runs.
      const onClickAnywhere = (e: Event) => {
        const t = e.target as Node | null;
        if (t && this.fabEl?.contains(t)) return;
        if (t && bubble.contains(t)) return;
        document.removeEventListener('click', onClickAnywhere, true);
        document.removeEventListener('touchstart', onClickAnywhere, true);
        dismiss();
      };
      // Defer registration so the click that opened the greeting
      // doesn't immediately dismiss it.
      requestAnimationFrame(() => {
        document.addEventListener('click', onClickAnywhere, true);
        document.addEventListener('touchstart', onClickAnywhere, true);
      });

      if (opts.autoDismissMs && opts.autoDismissMs > 0) {
        setTimeout(() => {
          document.removeEventListener('click', onClickAnywhere, true);
          document.removeEventListener('touchstart', onClickAnywhere, true);
          dismiss();
        }, opts.autoDismissMs);
      }
    });
  }

  /** Force-dismiss any greeting bubble currently on screen. */
  hideGreeting(): void {
    if (this.greetingEl) {
      this.greetingEl.remove();
      this.greetingEl = null;
    }
    if (this.fabEl) this.fabEl.removeAttribute('data-greeting');
    // NOTE: does not resolve the outstanding showGreeting() promise
    // if called externally — greetingDismiss is only cleared when
    // dismiss() runs from inside showGreeting.
    this.greetingDismiss = null;
  }

  /** One-shot celebration animation — bounces + shows `done` face,
   *  then returns to previous state after ~1.2s. Useful after the
   *  agent completes a positive action. */
  celebrate(): void {
    if (!this.fabEl) return;
    const prev = this.currentState;
    this.setState('done');
    setTimeout(() => {
      if (this.currentState === 'done') this.setState(prev === 'done' ? 'idle' : prev);
    }, 1200);
  }

  /**
   * Choreography: tilt the FAB TOWARD a target rect for ~700ms then
   * settle back. Reads as "the duck notices something happened over
   * there" — used when the subtitle bar opens, when a proactive
   * prompt materializes, or when a distant piece of UI catches the
   * user's attention.
   *
   * The tilt is a 12° head lean toward the target's centre; the CSS
   * animation composes with the idle bob so the head reads as
   * momentarily looking, not teleporting. Passing `null` cancels.
   */
  glanceAt(target: { x: number; y: number } | DOMRect | null): void {
    if (!this.fabEl) return;
    if (!target) {
      this.fabEl.removeAttribute('data-glance');
      this.fabEl.style.removeProperty('--dddk-fab-glance-angle');
      return;
    }
    const rect = this.fabEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const hasXY = 'x' in target && 'y' in target;
    const px = hasXY ? (target as { x: number }).x : (target as DOMRect).left + (target as DOMRect).width / 2;
    const py = hasXY ? (target as { y: number }).y : (target as DOMRect).top + (target as DOMRect).height / 2;
    const dx = px - cx;
    const dy = py - cy;
    // Only the horizontal component maps to head tilt; vertical is
    // small enough to ignore. Cap at ±12° so the tilt reads without
    // looking cartoonish.
    const angleDeg = Math.max(-12, Math.min(12, (dx / 200) * 12));
    void dy;
    this.fabEl.style.setProperty('--dddk-fab-glance-angle', `${angleDeg.toFixed(1)}deg`);
    this.fabEl.setAttribute('data-glance', '');
    setTimeout(() => { this.fabEl?.removeAttribute('data-glance'); }, 800);
  }

  // ─── chrome construction ───────────────────────────────────────

  private mountChrome(): void {
    const top = document.createElement('div');
    top.setAttribute(UI_ATTR, 'mobile-top');
    top.innerHTML = `
      <button type="button" class="mt-palette" aria-label="Open command palette">
        <span aria-hidden>🔍</span>
        <span class="mt-text">Palette</span>
      </button>
      <button type="button" class="mt-close" aria-label="Close mobile chrome">✕</button>
    `;
    top.querySelector<HTMLButtonElement>('.mt-palette')!.onclick = () => {
      this.dddk?.palette.toggle();
    };
    top.querySelector<HTMLButtonElement>('.mt-close')!.onclick = () => this.hide();
    document.body.appendChild(top);
    this.topBar = top;

    const bottom = document.createElement('div');
    bottom.setAttribute(UI_ATTR, 'mobile-bottom');
    bottom.innerHTML = `<button type="button" class="mt-center" aria-label="Voice / accept / reject"></button>`;
    const center = bottom.querySelector<HTMLButtonElement>('.mt-center')!;
    this.bindCenterButton(center);
    document.body.appendChild(bottom);
    this.bottomBar = bottom;

    // Click-through interception: when chrome visible AND user opted in,
    // taps on page elements fire palette_open with the tapped element's
    // selector. Default OFF — most users want the page to stay normal.
    // Toggled via `setDwellOnTap(true)` at runtime.
    const onPageClick = (e: MouseEvent | TouchEvent) => {
      if (!this.visible) return;
      if (!this.dwellOnTap) return; // page taps work normally
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Skip our own chrome.
      if (target.closest(`[${UI_ATTR}="mobile-top"]`) || target.closest(`[${UI_ATTR}="mobile-bottom"]`)) return;
      e.preventDefault();
      e.stopPropagation();
      const selector = inferSelector(target) || null;
      this.dddk?.palette.toggle(selector ?? target.textContent ?? '');
    };
    document.addEventListener('click', onPageClick, true);
    this.cleanups.push(() => document.removeEventListener('click', onPageClick, true));
  }

  /**
   * Center button mimics desktop Space gesture:
   *   - single tap         → accept
   *   - double tap (< 350) → reject
   *   - long press (200ms) → voice_start
   */
  private bindCenterButton(btn: HTMLButtonElement): void {
    const dddk = this.dddk;
    if (!dddk) return;

    const press = () => {
      this.longPressTimer = setTimeout(() => {
        this.longPressTimer = null;
        document.dispatchEvent(new CustomEvent('dddk:mobile-voice-start'));
      }, this.cfg.longPressMs);
    };

    const release = () => {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
        // Wasn't long-press → it's a tap. Distinguish single vs double.
        const now = Date.now();
        if (now - this.lastTapAt < this.cfg.doubleTapMs) {
          // Double tap
          if (this.singleTapTimer) {
            clearTimeout(this.singleTapTimer);
            this.singleTapTimer = null;
          }
          this.lastTapAt = 0;
          document.dispatchEvent(new CustomEvent('dddk:mobile-reject'));
        } else {
          // Maybe single — wait the double window
          this.lastTapAt = now;
          this.singleTapTimer = setTimeout(() => {
            this.singleTapTimer = null;
            document.dispatchEvent(new CustomEvent('dddk:mobile-accept'));
          }, this.cfg.doubleTapMs);
        }
      } else {
        // Long-press already fired voice_start. Voice end signal.
        document.dispatchEvent(new CustomEvent('dddk:mobile-voice-end'));
      }
    };

    btn.addEventListener('touchstart', (e) => { e.preventDefault(); press(); }, { passive: false });
    btn.addEventListener('touchend',   (e) => { e.preventDefault(); release(); }, { passive: false });
    btn.addEventListener('mousedown',  press);
    btn.addEventListener('mouseup',    release);
    btn.addEventListener('mouseleave', () => {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    });
  }

  // ─── swipe detector — rapid up/down reversals ───────────────────

  private bindSwipeDetector(): void {
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      this.samples = [{ y: t.clientY, t: Date.now(), dir: null }];
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0]!;
      const now = Date.now();
      const last = this.samples[this.samples.length - 1];
      if (!last) return;
      const dy = t.clientY - last.y;
      if (Math.abs(dy) < this.cfg.minSwipeDelta) return;
      const dir: 'up' | 'down' = dy < 0 ? 'up' : 'down';
      this.samples.push({ y: t.clientY, t: now, dir });
      this.trimSamples(now);
      if (this.countReversals() >= this.cfg.reversalsToTrigger) {
        this.samples = [];
        this.toggle();
      }
    };

    const onTouchEnd = () => {
      this.samples = [];
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove',  onTouchMove,  { passive: true });
    document.addEventListener('touchend',   onTouchEnd,   { passive: true });

    this.cleanups.push(() => document.removeEventListener('touchstart', onTouchStart));
    this.cleanups.push(() => document.removeEventListener('touchmove',  onTouchMove));
    this.cleanups.push(() => document.removeEventListener('touchend',   onTouchEnd));
  }

  private trimSamples(now: number): void {
    const cutoff = now - this.cfg.swipeWindowMs;
    this.samples = this.samples.filter((s) => s.t >= cutoff);
  }

  /** Count direction reversals (up→down or down→up) in current sample buffer. */
  private countReversals(): number {
    let reversals = 0;
    let prev: 'up' | 'down' | null = null;
    for (const s of this.samples) {
      if (s.dir && prev && s.dir !== prev) reversals += 1;
      if (s.dir) prev = s.dir;
    }
    return reversals;
  }
}

// ─── helpers ────────────────────────────────────────────────────────

function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
}

/**
 * Narrow viewport detection — mobile / tablet portrait. Independent of touch
 * capability so DevTools "responsive mode" on a desktop browser triggers it.
 */
function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 768px)').matches;
}

const DEFAULT_FAB_SVG = `
<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor"
  stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
  <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"/>
  <path d="M5 3v4M19 17v4M3 5h4M17 19h4"/>
</svg>
`.trim();

function ensureStyles(): void {
  injectScopedStyle(STYLE_ID, `
    /* Top semi-transparent bar */
    [${UI_ATTR}="mobile-top"] {
      position: fixed; top: 0; left: 0; right: 0;
      display: flex; align-items: center; justify-content: space-between;
      padding: env(safe-area-inset-top, 8px) 16px 8px 16px;
      background: var(--dddk-mobile-bar, rgba(255,255,255,0.78));
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-bottom: 1px solid var(--dddk-border, rgba(0,0,0,0.08));
      z-index: var(--dddk-z-bar, 9700);
      transform: translateY(-110%);
      transition: transform 0.25s cubic-bezier(0.2,0.8,0.2,1);
    }
    [${UI_ATTR}="mobile-top"][data-visible] { transform: translateY(0); }

    [${UI_ATTR}="mobile-top"] button {
      background: transparent; border: 0; cursor: pointer;
      font: inherit; color: var(--dddk-text, #18181b);
      padding: 8px 12px; border-radius: 8px;
      display: inline-flex; align-items: center; gap: 6px;
    }
    [${UI_ATTR}="mobile-top"] button:active {
      background: var(--dddk-accent-soft, rgba(139,115,85,0.12));
    }
    [${UI_ATTR}="mobile-top"] .mt-text { font-size: 13px; font-weight: 600; }
    [${UI_ATTR}="mobile-top"] .mt-close { font-size: 18px; line-height: 1; padding: 8px 14px; }

    /* Bottom semi-transparent bar with circular center button */
    [${UI_ATTR}="mobile-bottom"] {
      position: fixed; left: 0; right: 0; bottom: 0;
      display: flex; align-items: center; justify-content: center;
      padding: 12px 16px calc(env(safe-area-inset-bottom, 0px) + 12px) 16px;
      background: var(--dddk-mobile-bar, rgba(255,255,255,0.78));
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-top: 1px solid var(--dddk-border, rgba(0,0,0,0.08));
      z-index: var(--dddk-z-bar, 9700);
      transform: translateY(110%);
      transition: transform 0.25s cubic-bezier(0.2,0.8,0.2,1);
    }
    [${UI_ATTR}="mobile-bottom"][data-visible] { transform: translateY(0); }

    [${UI_ATTR}="mobile-bottom"] .mt-center {
      width: 64px; height: 64px; border-radius: 50%;
      background: var(--dddk-accent, #ec4899);
      border: 0; cursor: pointer;
      box-shadow: 0 6px 20px rgba(0,0,0,0.18);
      transition: transform 0.1s, box-shadow 0.15s;
    }
    [${UI_ATTR}="mobile-bottom"] .mt-center:active {
      transform: scale(0.92);
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }

    /* Floating Action Button (FAB) — default mobile entry point.
       Hosts can fully override via MobileFABConfig.style or by passing
       className and writing their own CSS.

       z-index = 10000 — ABOVE every other dddk surface (palette 9700,
       subtitle 9950, cursor 9990). The FAB is the user's only way to
       accept/reject the agent on mobile (replaces Space gesture), so
       any surface that hides it strands the user with no input path.
       Stays on top even when palette / confirm modal is open. */
    [${UI_ATTR}="mobile-fab"] {
      position: fixed;
      z-index: 10000;
      background: var(--dddk-accent, #ec4899);
      color: var(--dddk-text-on-accent, #fff);
      border: 0;
      cursor: pointer;
      box-shadow: var(--dddk-shadow-md, 0 6px 20px rgba(0,0,0,0.18));
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font: inherit;
      transition: transform 0.1s ease-out, box-shadow 0.15s ease-out;
      -webkit-tap-highlight-color: transparent;
    }
    [${UI_ATTR}="mobile-fab"]:active {
      transform: scale(0.94);
      box-shadow: var(--dddk-shadow-sm, 0 2px 8px rgba(0,0,0,0.2));
    }
    [${UI_ATTR}="mobile-fab"]:focus-visible {
      outline: 2px solid var(--dddk-accent, #ec4899);
      outline-offset: 3px;
    }

    /* ── mascot mode — face-image FAB ─────────────────────────────
       When alwaysVisible + faces are configured, the FAB shows a
       transparent duck-head PNG on every viewport. Face-mode buttons
       carry a data-face attribute (set by setFace) and a data-state
       attribute (set by setState). Both drive the CSS below.

       The desktop-hide media query is intentionally scoped to
       :not([data-face]) so face-mode FABs stay visible everywhere.
       Legacy (icon-only) FABs still hide on real desktop. */
    @media (min-width: 769px) and (hover: hover) and (pointer: fine) {
      [${UI_ATTR}="mobile-fab"]:not([data-face]) {
        display: none;
      }
    }

    /* Face-mode FAB — the yellow duck head is the whole visual.
       Zero button chrome so the transparent PNG reads as a floating
       character, not a button. Focus outline still works for a11y.
       data-enter="fresh" plays a one-shot pop-in scale animation on
       first mount (host sets it via a class or opt-in flag). */
    [${UI_ATTR}="mobile-fab"][data-face] {
      background: transparent;
      box-shadow: none;
      padding: 0;
      overflow: visible; /* so bob/celebrate + ripple bursts aren't clipped */
    }

    /* Pupils — two overlays that ride the neutral face's eye sockets
       and follow the cursor via JS-set transform. Coordinates are
       percentage-based on the 512×512 face artwork so the pupils sit
       correctly regardless of button size. Only visible when the face
       IS neutral (other faces have their own eyes baked in). */
    [${UI_ATTR}="fab-pupil"] {
      position: absolute;
      width: 12%;
      height: 12%;
      border-radius: 50%;
      background: #10182f;
      /* Pupils are inside the yellow head silhouette (~15% inset from
         edge). Left pupil ~28% from left, right ~60%. Vertical ~34%. */
      top: 34%;
      pointer-events: none;
      transition: opacity 0.24s ease-out;
      /* Small highlight — a tiny white dot on the upper-left of the
         pupil. Uses radial-gradient so it moves with the pupil. */
      background:
        radial-gradient(circle at 32% 30%, #ffffff 0 14%, transparent 15%),
        #10182f;
    }
    [${UI_ATTR}="fab-pupil"][data-side="left"]  { left: 28%; }
    [${UI_ATTR}="fab-pupil"][data-side="right"] { left: 60%; }
    /* Hide pupils when the face isn't neutral — the other faces have
       their own emotional eye art we don't want to double-print. */
    [${UI_ATTR}="mobile-fab"][data-face]:not([data-face="neutral"]) [${UI_ATTR}="fab-pupil"] {
      opacity: 0;
    }

    /* Ripple burst — replays on every setState. Emits a warm-yellow
       water ring outward from the FAB centre so the state change
       reads as a physical impulse, not a silent class swap. */
    [${UI_ATTR}="mobile-fab"][data-face] > .dddk-fab-ripple {
      /* placeholder for a proper JS-injected ripple; the CSS below
         handles the CSS-only version via data-ripple. */
    }
    [${UI_ATTR}="mobile-fab"][data-face][data-ripple]::before {
      /* Overrides the scroll-ripple ::before that also uses this
         selector — replays the burst regardless of scroll state. */
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      animation: dddk-fab-state-ripple 900ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
      pointer-events: none;
    }
    @keyframes dddk-fab-state-ripple {
      0%   { box-shadow:
              0 0 0 0  rgba(255,220,80,0.75),
              0 0 0 0  rgba(255,220,80,0.55);
            }
      100% { box-shadow:
              0 0 0 30px rgba(255,220,80,0),
              0 0 0 18px rgba(255,220,80,0);
            }
    }
    [${UI_ATTR}="mobile-fab"][data-face][data-enter="fresh"] {
      animation: dddk-fab-enter 950ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }
    /* Entrance choreography (Disney's twelve principles applied):
         · anticipation — drop below start position + squash
         · action     — spring up past target (overshoot)
         · follow-through — settle back with damped oscillation
       Not just "scale 0 → 1 with a bounce". Real feel-alive motion. */
    @keyframes dddk-fab-enter {
      0%   { transform: translateY(30px) scale(0.4, 0.55); opacity: 0; }
      20%  { transform: translateY(18px) scale(0.7, 0.65); opacity: 0.6; }
      45%  { transform: translateY(-12px) scale(1.14, 0.94); opacity: 1; }
      65%  { transform: translateY(4px)  scale(0.94, 1.08); opacity: 1; }
      82%  { transform: translateY(-2px) scale(1.03, 0.98); }
      100% { transform: translateY(0)    scale(1); }
    }
    [${UI_ATTR}="mobile-fab"][data-face]:active {
      transform: scale(0.92);
    }

    /* Glance-at choreography: on setState-triggered attention (host
       calls glanceAt(rect)), the head briefly leans horizontally
       toward the target. Overrides idle bob for ~800ms; smooth
       cubic-bezier so the tilt feels like a natural head turn. */
    @keyframes dddk-fab-glance {
      0%   { transform: rotate(0); }
      40%  { transform: rotate(var(--dddk-fab-glance-angle, 0deg)); }
      100% { transform: rotate(0); }
    }
    [${UI_ATTR}="mobile-fab"][data-face][data-glance] {
      animation: dddk-fab-glance 800ms cubic-bezier(0.4, 0, 0.2, 1) !important;
    }

    /* Idle bob — physics-inspired float on water: up-down coupled
       with a small rotation swing so the head reads as bobbing on
       a wave rather than an elevator ride. Non-integer duration
       decouples the phase from other on-screen loops. */
    @keyframes dddk-fab-bob {
      0%   { transform: translateY(0)    rotate(-1.2deg); }
      25%  { transform: translateY(-3px) rotate(0deg); }
      50%  { transform: translateY(-4px) rotate(1.4deg); }
      75%  { transform: translateY(-2px) rotate(0.4deg); }
      100% { transform: translateY(0)    rotate(-1.2deg); }
    }
    [${UI_ATTR}="mobile-fab"][data-state="idle"] {
      animation: dddk-fab-bob 3.4s ease-in-out infinite;
    }

    /* Thinking — the duck no longer spins (rotating a face read as
       vertigo). Instead a **conic-gradient progress arc** orbits the
       FAB, giving a proper "cooking" feel while the face stays
       upright. Face already switches to the thinking expression via
       setState. */
    [${UI_ATTR}="mobile-fab"][data-state="thinking"] {
      animation: dddk-fab-listen 1.8s ease-in-out infinite;
    }
    [${UI_ATTR}="mobile-fab"][data-state="thinking"]::after {
      content: '';
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      /* Circular sweeping arc: a bright yellow wedge orbits the FAB.
         The wedge is drawn via conic-gradient, then masked into a ring
         via inset shadow / border trick. */
      background: conic-gradient(
        from 0deg,
        rgba(255,220,80,0.9) 0deg,
        rgba(255,220,80,0.9) 60deg,
        transparent 100deg,
        transparent 360deg
      );
      -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3.5px), #000 calc(100% - 3px));
              mask: radial-gradient(farthest-side, transparent calc(100% - 3.5px), #000 calc(100% - 3px));
      animation: dddk-fab-orbit 1.4s linear infinite;
      pointer-events: none;
    }
    @keyframes dddk-fab-orbit {
      to { transform: rotate(360deg); }
    }
    /* Legacy spin keyframe retained (used by preview page CSS mirror). */
    @keyframes dddk-fab-spin {
      to { transform: rotate(360deg); }
    }

    /* Listening — SONAR animation. Two rings emanate outward from
       the FAB edge with staggered timing, plus a warm yellow ambient
       glow. Reads as "radar actively listening" — voice is one of
       the most-used interactions and deserves a distinct state, not
       another subtle bob. */
    @keyframes dddk-fab-listen {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.06); }
    }
    @keyframes dddk-fab-sonar {
      0%   { transform: scale(1);   opacity: 0.75; }
      60%  { opacity: 0.35; }
      100% { transform: scale(2.35); opacity: 0; }
    }
    [${UI_ATTR}="mobile-fab"][data-state="listening"] {
      animation: dddk-fab-listen 1.6s ease-in-out infinite;
      filter: drop-shadow(0 0 20px rgba(255,220,80,0.7));
    }
    /* Two-ring sonar via ::before + ::after. Both loop 1.6s but with
       an 800ms stagger so a new ring emerges just as the previous
       one is halfway out — reads as continuous emanation. */
    [${UI_ATTR}="mobile-fab"][data-state="listening"]::before {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      border: 2.5px solid rgba(255,220,80,0.85);
      pointer-events: none;
      animation: dddk-fab-sonar 1.6s ease-out infinite;
    }
    [${UI_ATTR}="mobile-fab"][data-state="listening"]::after {
      content: '';
      position: absolute;
      inset: -2px;
      border-radius: 50%;
      border: 2.5px solid rgba(255,220,80,0.85);
      pointer-events: none;
      opacity: 0;
      transform: scale(1);
      animation: dddk-fab-sonar 1.6s ease-out infinite;
      animation-delay: 0.8s;
    }

    /* Voice-hold — balloon breathe. The FAB rhythmically inflates and
       deflates while held, reads as "breathing pressure" rather than
       a one-shot inflate. Loops until state changes back to idle. The
       warm halo tints the glow yellow-orange (duck accent) instead of
       pink so recording doesn't feel like an error. */
    @keyframes dddk-fab-balloon {
      0%, 100% { transform: scale(1); }
      50%      { transform: scale(1.32); }
    }
    [${UI_ATTR}="mobile-fab"][data-state="voice-hold"] {
      animation: dddk-fab-balloon 1.4s ease-in-out infinite;
      filter: drop-shadow(0 0 22px rgba(255,190,60,0.55));
    }

    /* Done — quick pop bounce, then holds (setState will clear). */
    @keyframes dddk-fab-pop {
      0%   { transform: scale(1); }
      40%  { transform: scale(1.18); }
      70%  { transform: scale(0.94); }
      100% { transform: scale(1); }
    }
    [${UI_ATTR}="mobile-fab"][data-state="done"] {
      animation: dddk-fab-pop 0.7s ease-out;
    }

    /* Error — small horizontal shake. */
    @keyframes dddk-fab-shake {
      0%, 100% { transform: translateX(0); }
      20%      { transform: translateX(-4px); }
      40%      { transform: translateX(4px); }
      60%      { transform: translateX(-3px); }
      80%      { transform: translateX(3px); }
    }
    [${UI_ATTR}="mobile-fab"][data-state="error"] {
      animation: dddk-fab-shake 0.5s ease-in-out;
    }

    /* Scroll ripple — a warm-yellow water ring pulses outward from
       the FAB whenever the page scrolls, as if the duck is bobbing
       on ripples from the scroll motion. Host toggles data-scrolling
       on/off around scroll events; the pseudo animation replays each
       toggle so rapid scrolling reads as continuous ripples. */
    [${UI_ATTR}="mobile-fab"][data-face]::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      box-shadow: 0 0 0 0 rgba(255,220,80,0);
      pointer-events: none;
    }
    [${UI_ATTR}="mobile-fab"][data-face][data-scrolling]::before {
      animation: dddk-fab-scroll-ripple 620ms ease-out;
    }
    @keyframes dddk-fab-scroll-ripple {
      0%   { box-shadow: 0 0 0 0    rgba(255,220,80,0.55); }
      100% { box-shadow: 0 0 0 22px rgba(255,220,80,0);    }
    }

    /* Hover pulse ring — non-blocking pseudo halo that expands on
       cursor hover, giving desktop users an affordance without
       decorating the FAB itself. */
    [${UI_ATTR}="mobile-fab"][data-face]::after {
      content: '';
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      border: 2px solid rgba(255,220,80,0.55);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s ease-out, transform 0.35s ease-out;
    }
    [${UI_ATTR}="mobile-fab"][data-face]:hover::after {
      opacity: 1;
      transform: scale(1.08);
    }

    /* ── HERO greeting (capsule/pill) ─────────────────────────────
       The FAB circle STRETCHES horizontally into a translucent
       yellow capsule. The duck face stays on the right (its original
       corner); the greeting text fills the leftward space that opens
       up. Reads as "the FAB itself is speaking" — no separate bubble
       to fight with layout.

       All shape / colour / motion is CSS-variable-controlled so hosts
       can retheme without touching the SDK:
         --dddk-fab-hero-width          capsule expanded width (default 460px)
         --dddk-fab-hero-height         capsule height (default 88px)
         --dddk-fab-hero-radius         capsule end radius (default 44px)
         --dddk-fab-hero-bg             capsule fill (default warm-yellow gradient)
         --dddk-fab-hero-fg             text colour inside (default #1a2a4a)
         --dddk-fab-hero-padding        content padding (default 14px 90px 14px 22px)
         --dddk-fab-hero-shadow         drop shadow around capsule
         --dddk-fab-hero-transition-ms  morph timing (default 520ms)
         --dddk-fab-hero-easing         morph easing (default spring-out) */
    [${UI_ATTR}="mobile-fab"][data-hero-active] {
      width: var(--dddk-fab-hero-width, min(460px, calc(100vw - 40px))) !important;
      height: var(--dddk-fab-hero-height, 88px) !important;
      border-radius: var(--dddk-fab-hero-radius, 44px) !important;
      background: var(--dddk-fab-hero-bg,
        linear-gradient(135deg,
          rgba(255,244,196,0.98) 0%,
          rgba(255,232,150,0.95) 60%,
          rgba(255,220,100,0.92) 100%
        )
      ) !important;
      box-shadow: var(--dddk-fab-hero-shadow, 0 12px 32px rgba(255,180,40,0.35), 0 4px 12px rgba(0,0,0,0.08)) !important;
      overflow: hidden !important;
      transform: none !important;
      animation: none !important;
      transition:
        width  var(--dddk-fab-hero-transition-ms, 520ms) var(--dddk-fab-hero-easing, cubic-bezier(0.34, 1.4, 0.64, 1)),
        height var(--dddk-fab-hero-transition-ms, 520ms) var(--dddk-fab-hero-easing, cubic-bezier(0.34, 1.4, 0.64, 1)),
        border-radius var(--dddk-fab-hero-transition-ms, 520ms) var(--dddk-fab-hero-easing, cubic-bezier(0.34, 1.4, 0.64, 1)),
        background 320ms ease-out,
        box-shadow 320ms ease-out !important;
      backdrop-filter: blur(8px) saturate(1.1);
      -webkit-backdrop-filter: blur(8px) saturate(1.1);
    }
    /* Duck face inside the pill — anchored to the right edge (its
       original corner). Sized down from full-FAB to leave room for
       the text. */
    [${UI_ATTR}="mobile-fab"][data-hero-active] > span {
      position: absolute !important;
      right: var(--dddk-fab-hero-face-inset, 10px);
      top: 50%;
      transform: translateY(-50%);
      width: var(--dddk-fab-hero-face-size, 68px) !important;
      height: var(--dddk-fab-hero-face-size, 68px) !important;
    }
    [${UI_ATTR}="mobile-fab"][data-hero-active] > span > img {
      width: 100% !important;
      height: 100% !important;
      object-fit: contain;
    }
    /* Greeting text panel — fills the pill's non-face half. */
    [${UI_ATTR}="fab-hero-content"] {
      position: absolute;
      inset: 0;
      padding: var(--dddk-fab-hero-padding, 12px 90px 12px 22px);
      color: var(--dddk-fab-hero-fg, #1a2a4a);
      font: var(--dddk-fab-hero-font, 600 14px/1.45
        var(--dddk-font, -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang TC", sans-serif));
      letter-spacing: 0.005em;
      text-align: left;
      opacity: 0;
      transform: translateX(-8px);
      pointer-events: none;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: var(--dddk-fab-hero-gap, 4px);
      overflow: hidden;
      transition:
        opacity 260ms ease-out var(--dddk-fab-hero-content-delay, 220ms),
        transform 320ms ease-out var(--dddk-fab-hero-content-delay, 220ms);
    }
    [${UI_ATTR}="mobile-fab"][data-hero-active] [${UI_ATTR}="fab-hero-content"] {
      opacity: 1;
      transform: translateX(0);
    }
    /* Content-provided keycap-style badges (used by hosts inlining
       Ctrl / K / Space etc.). */
    [${UI_ATTR}="fab-hero-content"] kbd {
      display: inline-block;
      padding: 1px 6px;
      margin: 0 1px;
      background: rgba(255,255,255,0.85);
      color: var(--dddk-fab-hero-fg, #1a2a4a);
      border: 1px solid rgba(20,30,60,0.15);
      border-radius: 4px;
      box-shadow: 0 1px 0 rgba(20,30,60,0.1);
      font: 600 12px ui-monospace, "SFMono-Regular", Menlo, monospace;
    }

    /* Dismiss: pill contracts back to circle. Content fades first,
       then the width/height/radius run back to the FAB defaults. */
    [${UI_ATTR}="mobile-fab"][data-hero-dismissing] {
      transition:
        width  var(--dddk-fab-hero-transition-ms, 520ms) cubic-bezier(0.5, 0, 0.2, 1),
        height var(--dddk-fab-hero-transition-ms, 520ms) cubic-bezier(0.5, 0, 0.2, 1),
        border-radius var(--dddk-fab-hero-transition-ms, 520ms) cubic-bezier(0.5, 0, 0.2, 1),
        background 320ms ease-out,
        box-shadow 320ms ease-out !important;
      overflow: hidden !important;
    }
    [${UI_ATTR}="mobile-fab"][data-hero-dismissing] [${UI_ATTR}="fab-hero-content"] {
      opacity: 0;
      transition: opacity 160ms ease-out;
    }

    /* ── HERO greeting (legacy separate-sprite) ───────────────────
       Old design left in place for hosts wiring their own hero
       elements; unused by the current showHeroGreeting.  Kept to
       avoid breaking third-party CSS overrides. */
    [${UI_ATTR}="fab-hero"] {
      position: fixed;
      width: min(30vw, 240px);
      height: min(30vw, 240px);
      /* Duck sits IN FRONT of the bubble — semantically the character
         is the foreground, the text label is a soft caption behind it. */
      z-index: 10003;
      pointer-events: none;
      opacity: 0;
      transform-origin: bottom right;
      transform: scale(0.15) translate(60%, 60%);
      transition:
        opacity 320ms ease-out,
        transform 720ms cubic-bezier(0.16, 1.2, 0.3, 1);
    }
    [${UI_ATTR}="fab-hero"][data-corner*="left"]  { transform-origin: bottom left;  transform: scale(0.15) translate(-60%, 60%); }
    [${UI_ATTR}="fab-hero"][data-corner*="top"]   { transform-origin: top right;    transform: scale(0.15) translate(60%, -60%); }
    [${UI_ATTR}="fab-hero"][data-corner*="top"][data-corner*="left"] { transform-origin: top left; transform: scale(0.15) translate(-60%, -60%); }
    [${UI_ATTR}="fab-hero"][data-visible] {
      opacity: 1;
      transform: scale(1) translate(0, 0);
      animation: dddk-fab-hero-bob 3.6s ease-in-out infinite 720ms;
    }
    [${UI_ATTR}="fab-hero"][data-dismissing] {
      opacity: 0;
      transform: scale(0.15) translate(60%, 60%);
      transition:
        opacity 240ms ease-in 60ms,
        transform 420ms cubic-bezier(0.5, 0, 0.75, 0);
    }
    [${UI_ATTR}="fab-hero"] img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      display: block;
      filter: drop-shadow(0 12px 24px rgba(30,60,120,0.18));
    }
    @keyframes dddk-fab-hero-bob {
      /* Larger sprite = smaller rotation angle. What reads as a subtle
         float on a 52px FAB reads as a drunken wobble at 240px.
         Kept translation but toned rotation to a hair. */
      0%   { transform: translateY(0)    rotate(-0.6deg); }
      50%  { transform: translateY(-6px) rotate(0.6deg); }
      100% { transform: translateY(0)    rotate(-0.6deg); }
    }

    /* HERO greeting text — deliberately NOT a rigid chat bubble. Text
       floats over the page with a soft warm-yellow radial gradient
       behind it (fades to transparent at edges) so it reads clearly
       against any background without a hard box drawn around it. No
       tail, no border. Feels like a caption or a hand-lettered sign
       hovering near the mascot rather than a UI chat window. */
    [${UI_ATTR}="fab-hero-bubble"] {
      position: fixed;
      /* Behind the duck (10003). The bubble is a soft caption; the
         duck is the character. Duck occlusion feels natural. */
      z-index: 10001;
      max-width: min(60vw, 380px);
      padding: 22px 28px;
      /* Dense warm-white centre so text sits on a fully opaque field —
         no bleed-through from posters / images beneath. Only the outer
         ~15% fades to transparent so the edge dissolves softly. */
      background:
        radial-gradient(
          ellipse at center,
          #fffaea 0%,
          rgba(255,246,214,0.98) 55%,
          rgba(255,240,200,0.9)  82%,
          rgba(255,240,200,0)    100%
        );
      /* Blur whatever's behind so any bleed at the fade edge reads as
         atmosphere, not competing content. */
      backdrop-filter: blur(10px) saturate(1.15);
      -webkit-backdrop-filter: blur(10px) saturate(1.15);
      color: #18181b;
      font: 600 16.5px/1.55 var(--dddk-font, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
      letter-spacing: 0.005em;
      text-shadow: 0 1px 0 rgba(255,255,255,0.6);
      /* Soft warm glow — reads as light, not a chat box. */
      filter: drop-shadow(0 10px 28px rgba(255,180,40,0.28));
      opacity: 0;
      transform: translateY(20px) scale(0.85);
      transform-origin: bottom right;
      transition:
        opacity 300ms ease-out 280ms,
        transform 620ms cubic-bezier(0.34, 1.6, 0.64, 1) 280ms;
      pointer-events: auto;
    }
    /* kbd-style key inside the greeting text — makes "Ctrl+K" look
       like an actual keycap so users read it as a keyboard shortcut. */
    [${UI_ATTR}="fab-hero-bubble"] kbd {
      display: inline-block;
      padding: 2px 8px;
      margin: 0 2px;
      background: #ffffff;
      color: #1a2a4a;
      border: 1px solid rgba(20,30,60,0.18);
      border-radius: 5px;
      box-shadow: 0 1px 0 rgba(20,30,60,0.18);
      font: 600 13.5px ui-monospace, "SFMono-Regular", Menlo, monospace;
      vertical-align: 2px;
    }
    [${UI_ATTR}="fab-hero-bubble"][data-visible] {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    [${UI_ATTR}="fab-hero-bubble"][data-side="left"] { transform-origin: bottom right; }
    [${UI_ATTR}="fab-hero-bubble"][data-side="right"] { transform-origin: bottom left; }

    /* Greeting bubble — anchored beside the FAB, tail points at it. */
    [${UI_ATTR}="mobile-fab-greeting"] {
      position: fixed;
      z-index: 10001;
      max-width: min(70vw, 340px);
      padding: 12px 16px;
      background: var(--dddk-surface, #ffffff);
      color: var(--dddk-text, #18181b);
      border: 1px solid var(--dddk-border, rgba(0,0,0,0.08));
      border-radius: 16px;
      box-shadow: 0 12px 32px rgba(0,0,0,0.14), 0 2px 6px rgba(0,0,0,0.06);
      font: 14px/1.4 var(--dddk-font, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
      opacity: 0;
      transform: translateY(14px) scale(0.7);
      /* Spring-mimicking cubic-bezier: overshoots at ~1.02× then
         settles. Feels bouncy without a physics library. */
      transition:
        opacity 0.28s ease-out,
        transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
      pointer-events: none;
    }
    [${UI_ATTR}="mobile-fab-greeting"][data-visible] {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    /* Bubble tail — small pointer toward the FAB. Direction set via
       data-side (matches which side of the FAB we're anchored on). */
    [${UI_ATTR}="mobile-fab-greeting"][data-side="left"] {
      transform-origin: bottom right;
    }
    [${UI_ATTR}="mobile-fab-greeting"][data-side="left"]::after {
      content: '';
      position: absolute;
      right: -6px;
      bottom: 18px;
      width: 12px;
      height: 12px;
      background: var(--dddk-surface, #ffffff);
      border-right: 1px solid var(--dddk-border, rgba(0,0,0,0.08));
      border-bottom: 1px solid var(--dddk-border, rgba(0,0,0,0.08));
      transform: rotate(-45deg);
    }
    [${UI_ATTR}="mobile-fab-greeting"][data-side="right"] {
      transform-origin: bottom left;
    }
    [${UI_ATTR}="mobile-fab-greeting"][data-side="right"]::after {
      content: '';
      position: absolute;
      left: -6px;
      bottom: 18px;
      width: 12px;
      height: 12px;
      background: var(--dddk-surface, #ffffff);
      border-left: 1px solid var(--dddk-border, rgba(0,0,0,0.08));
      border-bottom: 1px solid var(--dddk-border, rgba(0,0,0,0.08));
      transform: rotate(45deg);
    }

    /* Respect motion preferences — kill all decorative loops. */
    @media (prefers-reduced-motion: reduce) {
      [${UI_ATTR}="mobile-fab"][data-state="idle"],
      [${UI_ATTR}="mobile-fab"][data-state="thinking"],
      [${UI_ATTR}="mobile-fab"][data-state="listening"] {
        animation: none;
      }
    }
  `);
}
